import React, { useState, useEffect } from 'react';
import { X, Save, Plus, Trash2, AlertTriangle } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import api from '../api';
import { toast } from 'sonner';

const PAYER_OPTIONS = [
  'CIGNA', 'Medicare', 'Medicaid', 'Blue Cross Blue Shield',
  'Aetna', 'United Healthcare', 'Humana', 'Kaiser Permanente',
  'Anthem', 'Tricare', 'Molina Healthcare', 'WellCare',
  'Ambetter', 'Centene', 'Oscar Health', 'Bright Health',
  'Devoted Health', 'Clover Health', 'Alignment Healthcare'
];

const INSURANCE_TYPES = ['Primary', 'Secondary', 'Tertiary'];
const RELATIONSHIP_OPTIONS = ['Self', 'Spouse', 'Child', 'Other'];

const InsuranceFormModal = ({ patient, onClose, onSave }) => {
  const [saving, setSaving] = useState(false);
  const [insuranceData, setInsuranceData] = useState({
    insuranceType: 'Primary',
    payerName: patient?.insurance || '',
    payerId: patient?.insurancePayerId || '',
    memberId: patient?.insuranceMemberId || '',
    groupNumber: patient?.insuranceGroupNumber || '',
    policyHolder: patient?.insurancePolicyHolder || `${patient?.firstName || ''} ${patient?.lastName || ''}`,
    policyHolderDOB: patient?.insurancePolicyHolderDOB || '',
    relationship: patient?.insuranceRelationship || 'Self',
    effectiveDate: patient?.insuranceEffectiveDate || '',
    terminationDate: patient?.insuranceTerminationDate || '',
    priorAuthRequired: patient?.insurancePriorAuthRequired || false
  });

  const handleChange = (field, value) => {
    setInsuranceData(prev => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!insuranceData.payerName) {
      toast.error('Please select a Payer Name');
      return;
    }

    setSaving(true);
    try {
      // Use dedicated insurance update endpoint
      const response = await api.put(`/patients/${patient.id}/insurance`, {
        insurance: insuranceData.payerName,
        insuranceType: insuranceData.insuranceType,
        insurancePayerId: insuranceData.payerId,
        insuranceMemberId: insuranceData.memberId,
        insuranceGroupNumber: insuranceData.groupNumber,
        insurancePolicyHolder: insuranceData.policyHolder,
        insurancePolicyHolderDOB: insuranceData.policyHolderDOB,
        insuranceRelationship: insuranceData.relationship,
        insuranceEffectiveDate: insuranceData.effectiveDate,
        insuranceTerminationDate: insuranceData.terminationDate,
        insurancePriorAuthRequired: insuranceData.priorAuthRequired
      });
      
      if (response.data.success) {
        toast.success('Insurance saved successfully');
        onSave(response.data.patient);
        onClose();
      }
    } catch (error) {
      console.error('Insurance save error:', error);
      toast.error('Failed to save insurance');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4" data-testid="insurance-form-modal">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b bg-[#3498db] text-white rounded-t-lg">
          <h2 className="text-xl font-semibold">Add Insurance</h2>
          <button onClick={onClose} className="p-1 hover:bg-white/20 rounded">
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Patient Info Bar */}
        <div className="bg-gray-50 px-6 py-3 border-b">
          <p className="font-medium text-gray-800">{patient?.lastName}, {patient?.firstName} {patient?.middleName || ''}</p>
          <p className="text-sm text-gray-500">PAN #{patient?.pan || '-'} | DOB: {patient?.dateOfBirth ? new Date(patient.dateOfBirth).toLocaleDateString() : '-'}</p>
        </div>

        {/* Form */}
        <div className="p-6 space-y-4">
          {/* Row 1: Insurance Type & Payer Name */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">Insurance Type</Label>
              <Select value={insuranceData.insuranceType} onValueChange={(v) => handleChange('insuranceType', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Type" />
                </SelectTrigger>
                <SelectContent>
                  {INSURANCE_TYPES.map(type => (
                    <SelectItem key={type} value={type}>{type}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-gray-700">Payer Name <span className="text-red-500">*</span></Label>
              <Select value={insuranceData.payerName} onValueChange={(v) => handleChange('payerName', v)}>
                <SelectTrigger className="mt-1" data-testid="payer-name-select">
                  <SelectValue placeholder="e.g., Medicare, Aetna" />
                </SelectTrigger>
                <SelectContent>
                  {PAYER_OPTIONS.map(payer => (
                    <SelectItem key={payer} value={payer}>{payer}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 2: Payer ID, Member ID, Group Number */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-700">Payer ID</Label>
              <Input
                value={insuranceData.payerId}
                onChange={(e) => handleChange('payerId', e.target.value)}
                className="mt-1"
                placeholder="Enter Payer ID"
                data-testid="payer-id-input"
              />
            </div>
            <div>
              <Label className="text-gray-700">Member ID</Label>
              <Input
                value={insuranceData.memberId}
                onChange={(e) => handleChange('memberId', e.target.value)}
                className="mt-1"
                placeholder="Enter Member ID"
                data-testid="member-id-input"
              />
            </div>
            <div>
              <Label className="text-gray-700">Group Number</Label>
              <Input
                value={insuranceData.groupNumber}
                onChange={(e) => handleChange('groupNumber', e.target.value)}
                className="mt-1"
                placeholder="Enter Group Number"
                data-testid="group-number-input"
              />
            </div>
          </div>

          {/* Row 3: Policy Holder, Policy Holder DOB, Relationship */}
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Label className="text-gray-700">Policy Holder</Label>
              <Input
                value={insuranceData.policyHolder}
                onChange={(e) => handleChange('policyHolder', e.target.value)}
                className="mt-1"
                placeholder="Enter Policy Holder Name"
                data-testid="policy-holder-input"
              />
            </div>
            <div>
              <Label className="text-gray-700">Policy Holder DOB</Label>
              <Input
                type="date"
                value={insuranceData.policyHolderDOB}
                onChange={(e) => handleChange('policyHolderDOB', e.target.value)}
                className="mt-1"
                data-testid="policy-holder-dob-input"
              />
            </div>
            <div>
              <Label className="text-gray-700">Relationship</Label>
              <Select value={insuranceData.relationship} onValueChange={(v) => handleChange('relationship', v)}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select Relationship" />
                </SelectTrigger>
                <SelectContent>
                  {RELATIONSHIP_OPTIONS.map(rel => (
                    <SelectItem key={rel} value={rel}>{rel}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Row 4: Effective Date, Termination Date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-gray-700">Effective Date</Label>
              <Input
                type="date"
                value={insuranceData.effectiveDate}
                onChange={(e) => handleChange('effectiveDate', e.target.value)}
                className="mt-1"
                data-testid="effective-date-input"
              />
            </div>
            <div>
              <Label className="text-gray-700">Termination Date</Label>
              <Input
                type="date"
                value={insuranceData.terminationDate}
                onChange={(e) => handleChange('terminationDate', e.target.value)}
                className="mt-1"
                data-testid="termination-date-input"
              />
            </div>
          </div>

          {/* Prior Authorization Required */}
          <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
            <label className="flex items-center gap-3 cursor-pointer">
              <input
                type="checkbox"
                checked={insuranceData.priorAuthRequired}
                onChange={(e) => handleChange('priorAuthRequired', e.target.checked)}
                className="w-5 h-5 rounded border-gray-300 text-orange-500 focus:ring-orange-500"
                data-testid="prior-auth-checkbox"
              />
              <span className="text-orange-700 font-medium flex items-center gap-2">
                <AlertTriangle className="w-5 h-5" />
                Prior Authorization Required
              </span>
            </label>
          </div>
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-lg">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button 
            onClick={handleSave} 
            className="bg-orange-500 hover:bg-orange-600"
            disabled={saving}
            data-testid="save-insurance-btn"
          >
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Saving...' : 'Save Insurance'}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default InsuranceFormModal;
